import { useEffect, useRef } from 'react';
import { isConnectorAvailable } from '../lib/connector';
import { useMaestroStore } from '../store/maestroStore';
import { readSelectedUsbDac } from '../utils/usbDacNames';

const BUILT_IN_KEYWORDS = [
  'speakers', 'realtek', 'built-in', 'default', 'headphones', 'hdmi',
  'displayport', 'digital audio', 'microsoft sound mapper', 'communications',
  'macbook', 'internal', 'monitor',
];

function looksLikeUsbDac(label) {
  if (!label) return false;
  const l = label.toLowerCase();
  if (BUILT_IN_KEYWORDS.some((kw) => l.includes(kw))) return false;
  // Short or generic labels are likely built-in
  if (l.length < 5) return false;
  return true;
}

/**
 * Silently detect USB DACs via the browser's audio output enumeration
 * and auto-route all <audio> elements to the DAC via setSinkId.
 *
 * Runs once at mount. No user interaction required.
 * Skips when the Connector is active (Connector has its own device routing).
 */
let _globalDacDeviceId = readSelectedUsbDac()?.id || null;
let _enumerated = false;

export function getAutoSelectedDacId() {
  return _globalDacDeviceId;
}

/**
 * Apply the auto-selected DAC to an audio element.
 * Call this whenever a new audio element is created or source changes.
 */
export async function applyUsbDacToAudio(audioElement) {
  if (isConnectorAvailable()) return;
  if (!_globalDacDeviceId || !audioElement) return;
  if (typeof audioElement.setSinkId !== 'function') return;
  try {
    // Chromium returns a string for sinkId; skip if already set
    if (audioElement.sinkId === _globalDacDeviceId) return;
    await audioElement.setSinkId(_globalDacDeviceId);
  } catch (err) {
    console.warn('[AutoUsbDac] setSinkId failed:', err);
  }
}

async function enumerateAndSelect() {
  if (_enumerated) return;
  _enumerated = true;

  if (!navigator.mediaDevices?.enumerateDevices) return;
  if (typeof HTMLMediaElement.prototype.setSinkId !== 'function') return;

  try {
    let devices = await navigator.mediaDevices.enumerateDevices();
    let outputs = devices.filter((d) => d.kind === 'audiooutput' && d.deviceId !== 'default');

    // #850 — never unlock labels by requesting the microphone.

    // Restore a previously selected USB DAC before auto-detecting a new one.
    const saved = readSelectedUsbDac();
    if (saved?.id) {
      const savedDevice = outputs.find((d) => d.deviceId === saved.id);
      if (savedDevice) {
        _globalDacDeviceId = saved.id;
        console.log('[AutoUsbDac] Restored saved USB DAC:', saved.name, '→', saved.id);
        useMaestroStore.getState().setSelectedUsbDac({
          id: saved.id,
          name: saved.name || savedDevice.label,
          uid: saved.uid,
        });
        const audios = document.querySelectorAll('audio');
        for (const el of audios) {
          applyUsbDacToAudio(el);
        }
        return;
      }
    }

    // Find a non-built-in device that looks like a USB DAC
    const dac = outputs.find((d) => looksLikeUsbDac(d.label));
    if (!dac) return;

    _globalDacDeviceId = dac.deviceId;
    console.log('[AutoUsbDac] Detected USB DAC:', dac.label, '→', dac.deviceId);

    // Store it so the zone picker shows the selection
    useMaestroStore.getState().setSelectedUsbDac({
      id: dac.deviceId,
      name: dac.label,
    });

    // Apply to any existing audio elements immediately
    const audios = document.querySelectorAll('audio');
    for (const el of audios) {
      applyUsbDacToAudio(el);
    }
  } catch (err) {
    console.warn('[AutoUsbDac] Enumeration failed:', err);
  }
}

/**
 * Listen for device changes (plug/unplug) and re-evaluate.
 */
function watchDeviceChanges() {
  if (!navigator.mediaDevices) return () => {};
  const handler = () => {
    _enumerated = false;
    _globalDacDeviceId = readSelectedUsbDac()?.id || null;
    enumerateAndSelect();
  };
  navigator.mediaDevices.addEventListener('devicechange', handler);
  return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
}

export default function useAutoUsbDac() {
  const didRun = useRef(false);

  useEffect(() => {
    // Connector handles its own device routing
    if (isConnectorAvailable()) return;
    if (didRun.current) return;
    didRun.current = true;

    enumerateAndSelect();
    const cleanup = watchDeviceChanges();
    return cleanup;
  }, []);
}
